import type { Workspace } from '../../types/workspace'
import {
  getExploreWorkspaceSeed,
  toPreviewWorkspace,
} from '../exploreWorkspaceSeeds'
import type { ExploreWorkspaceSeed } from '../../explore_seeds/workspaceSeeds'
import { STRINGS_NLS } from '../../nls/strings_nls'

export type ExploreDeepLinkAction =
  | { type: 'NONE' }
  | {
      type: 'OPEN_PREVIEW'
      seedId: string
      previewWorkspace: Workspace
      landingView: 'TEACHING' | 'TRAINING' | 'OVERVIEW'
    }
  | {
      type: 'EXPLORE_NOT_FOUND'
      seedId: string
      toastMessage: string
      landingView: 'EXPLORE'
    }

export interface ExploreDeepLinkDeps {
  getSeed?: (id: string) => ExploreWorkspaceSeed | null | undefined
  toPreview?: (seed: ExploreWorkspaceSeed) => Workspace
  formatNotFoundToast?: (id: string) => string
}

/**
 * Consumes the `?explore=<seedId>` URL query parameter, clears it from history,
 * and returns the appropriate navigation action:
 * - OPEN_PREVIEW: known seed id -> open preview workspace and navigate to its dedicated hub
 *   (TEACHING when teaching is enabled, TRAINING for training-only demos, or OVERVIEW).
 * - EXPLORE_NOT_FOUND: unknown seed id -> navigate to EXPLORE hub and show a toast.
 * - NONE: no `explore` query parameter present.
 */
export function consumeExploreDeepLink(
  location: { search: string; pathname: string; hash?: string },
  history: { replaceState: (data: unknown, unused: string, url?: string | URL | null) => void; state?: unknown },
  deps?: ExploreDeepLinkDeps,
): ExploreDeepLinkAction {
  const searchParams = new URLSearchParams(location.search)
  const exploreParam = searchParams.get('explore')
  if (!exploreParam) {
    return { type: 'NONE' }
  }

  // Clear 'explore' param from the URL so a refresh does not reopen it
  searchParams.delete('explore')
  const remainingQuery = searchParams.toString() ? `?${searchParams.toString()}` : ''
  history.replaceState(
    history.state,
    '',
    `${location.pathname}${remainingQuery}${location.hash ?? ''}`,
  )

  const getSeed = deps?.getSeed ?? getExploreWorkspaceSeed
  const toPreview = deps?.toPreview ?? toPreviewWorkspace
  const formatToast = deps?.formatNotFoundToast ?? ((id: string) => STRINGS_NLS.NLSEduDemoSeedNotFoundToast.text.replace('{id}', id))

  const seed = getSeed(exploreParam)
  if (!seed) {
    return {
      type: 'EXPLORE_NOT_FOUND',
      seedId: exploreParam,
      toastMessage: formatToast(exploreParam),
      landingView: 'EXPLORE',
    }
  }

  const previewWorkspace = toPreview(seed)
  const modules = (seed as { modules?: Partial<Workspace['modules']> }).modules ?? previewWorkspace.modules

  let landingView: 'TEACHING' | 'TRAINING' | 'OVERVIEW' = 'OVERVIEW'
  if (modules?.teachingEnabled && !modules?.trainingEnabled) {
    landingView = 'TEACHING'
  } else if (modules?.trainingEnabled && !modules?.teachingEnabled) {
    landingView = 'TRAINING'
  }

  return {
    type: 'OPEN_PREVIEW',
    seedId: exploreParam,
    previewWorkspace,
    landingView,
  }
}
